import React, { useEffect } from "react";

import { useQueryClient } from "@tanstack/react-query";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import {
  EmailPreferences,
  getGetEmailPreferencesQueryKey,
  useGetEmailPreferences,
  useUpdateEmailPreferences,
  useValidateRecaptcha,
} from "@api";

import { usePartnerInfo } from "@utils";

import { Button, Notification, Text, Title } from "@ui/elements";
import { Checkbox } from "@ui/forms";
import { Stack } from "@ui/layout";

import { toaster } from "@components";

type UpdateEmailPreferencesFormValues = EmailPreferences;

export const UpdateEmailPreferences = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const partnerInfo = usePartnerInfo();
  const {
    mutateAsync: updateEmailPreferences,
    error,
    isPending: updateEmailPreferencesLoading,
  } = useUpdateEmailPreferences();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const {
    mutateAsync: validateRecaptcha,
    isPending: recaptchaLoading,
    data: recaptchaResponseData,
  } = useValidateRecaptcha();
  const token = searchParams.get("token") || "";
  const { data: emailPreferences, isLoading: emailPreferencesLoading } =
    useGetEmailPreferences(token, {
      query: {
        enabled: !!token,
      },
    });

  const form = useForm<UpdateEmailPreferencesFormValues>({
    defaultValues: {
      platformUpdates: emailPreferences?.platformUpdates,
      feedbackSurvey: emailPreferences?.feedbackSurvey,
      marketingEmails: emailPreferences?.marketingEmails,
      newsLetters: emailPreferences?.newsLetters,
    },
  });

  useEffect(() => {
    if (emailPreferences) {
      form.setValue("platformUpdates", emailPreferences.platformUpdates);
      form.setValue("marketingEmails", emailPreferences.marketingEmails);
      form.setValue("newsLetters", emailPreferences.newsLetters);
      form.setValue("feedbackSurvey", emailPreferences.feedbackSurvey);
    }
  }, [emailPreferences]);

  const update = async (values: UpdateEmailPreferencesFormValues) => {
    if (!executeRecaptcha) {
      console.log("Execute recaptcha not yet available");
      return;
    }

    const token = await executeRecaptcha("update_email_preferences");
    const recaptchaResponse = await validateRecaptcha({ data: { token } });

    if (!recaptchaResponse.success) {
      console.log("Failed to validate reCAPTCHA. Please try again.");
      return;
    }

    await updateEmailPreferences({
      id: searchParams.get("token") || "",
      data: values,
    });

    void queryClient.invalidateQueries({
      queryKey: getGetEmailPreferencesQueryKey(token),
    });

    toaster.success({
      title: t("messages.successful-update"),
      message: t("messages.update-email-preferences-success"),
    });
  };

  const onSubmit = async (values: UpdateEmailPreferencesFormValues) => {
    void update(values);
  };

  const handleUnsubscribeFromAll = () => {
    void update({
      platformUpdates: false,
      feedbackSurvey: false,
      marketingEmails: false,
      newsLetters: false,
    });
  };
  const hasError = !!error?.response?.data.error;

  const loading = recaptchaLoading || updateEmailPreferencesLoading;

  return (
    <form onSubmit={form.handleSubmit(onSubmit)}>
      <Stack direction="column" gap="xl">
        <Title size={5}>{t("update-your-email-preferences")}</Title>

        <Stack direction="column">
          <Checkbox
            isCentered={false}
            disabled={emailPreferencesLoading}
            {...form.register("platformUpdates")}
          >
            <Text weight="bold">{t("email-preferences.platform-updates")}</Text>
            <Text variant="secondary">
              {t("email-preferences.platform-updates-description")}
            </Text>
          </Checkbox>
          <Checkbox
            isCentered={false}
            disabled={emailPreferencesLoading}
            {...form.register("feedbackSurvey")}
          >
            <Text weight="bold">
              {t("email-preferences.feedback-and-surveys")}
            </Text>
            <Text variant="secondary">
              {t("email-preferences.feedback-and-surveys-description")}
            </Text>
          </Checkbox>
          <Checkbox
            isCentered={false}
            disabled={emailPreferencesLoading}
            {...form.register("marketingEmails")}
          >
            <Text weight="bold">{t("email-preferences.marketing")}</Text>
            <Text variant="secondary">
              {t("email-preferences.marketing-description")}
            </Text>
          </Checkbox>
          <Checkbox
            isCentered={false}
            disabled={emailPreferencesLoading}
            {...form.register("newsLetters")}
          >
            <Text weight="bold">{t("email-preferences.newsletters")}</Text>
            <Text variant="secondary">
              {t("email-preferences.newsletters-description")}
            </Text>
          </Checkbox>
        </Stack>

        <Stack direction="column">
          {hasError && (
            <Notification color="danger">
              <Stack direction="column" gap="lg">
                <Stack direction="column" gap="xxs">
                  <Text weight="bold">{error.response?.data.message}</Text>
                  <Text>
                    {t(error.response?.data.error || "") ||
                      t("messages.unexpected-error")}
                  </Text>
                </Stack>

                <Text
                  dangerouslySetInnerHTML={{
                    __html: t("messages.try-again-later-email", {
                      email: partnerInfo.contact.email,
                    }),
                  }}
                />
              </Stack>
            </Notification>
          )}
          {!!recaptchaResponseData?.error && (
            <Notification color="danger">
              <Text>{t(recaptchaResponseData.error)}</Text>
            </Notification>
          )}
          <Stack direction="column" gap="xs">
            <Button color="primary" isLoading={loading}>
              {t("labels.update-email-preferences")}
            </Button>
            <Button
              variant="text"
              type="button"
              disabled={loading}
              onClick={() => handleUnsubscribeFromAll()}
            >
              {t("labels.unsubscribe-from-all")}
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </form>
  );
};
